import React, { useEffect, useState, useContext, useRef } from 'react'
import type { GroupItem } from '../../../../typings'
import useWindowSize from '../../utils/useWindowSize'
import serverData from '../../utils/server-data'
import { useGetGroupDetailQuery } from '../../services/groupApi'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  clearHandledError,
  setHandledError,
} from '../../store/slices/handledErrorSlice'
import { getContentStyles } from '../../utils/styles/contentStyle'
import { getContributionUserURL } from '../../utils/links/contributeLinks'
import {
  setCreateGroupFlow,
  toggleCloseGroup,
  toggleIntro,
  toggleUpdateGroup,
} from '../../store/slices/sheetsSlice'
import { GROUP_STATUS, HOME_PATH, SHEET_SOURCE } from '../../constants'
import CoverImage from '../cover-image/CoverImage'
import Update from '../update/Update'
import { getTransferURL } from '../../utils/links/transferLinks'
import getExternalData from '../../utils/external/handleExternalData'
import { useLocalizedContent } from '../../hooks/useLocalizedContent'
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner'
import { initializeForm } from '../../store/slices/groupFormSlice'
import { getGroupDetailPageVariant, tracking } from '../../lib/analytics/fpti'
import Events from '../../lib/analytics/eventTags.js'
import { parseFormattedDate } from '../../utils/localization/format_worldready'
import { Context as WorldReadyContext } from '@paypalcorp/worldready-react'
import { isDetailsV1Dot1Enabled } from '../../utils/features'
import { navigate } from '@reach/router'
import GroupDetailV1 from './v1'
import GroupDetailV1Dot1 from './v1.1' // Import needs to be PascalCase

interface GroupDetailProps {
  groupId: string
}

interface RenderParams {
  isSmallerScreen: boolean
  isShareSheetOpen: boolean
  setIsShareSheetOpen: React.Dispatch<React.SetStateAction<boolean>>
  groupId: string
  groupItem: GroupItem
  isAdmin: boolean
  isPayPalUser: boolean
  handleClickOnEdit: () => void
  handleClickOnCloseGroup: () => void
  handleClickOnReport: () => void
  handleContributeAsGuest: () => void
  handleContributeAsUser: () => void
  navigateHome: () => void
  createGroup: () => void
  handleClickOnTransfer: () => void
  onClickMoreOptions: () => void
}

const renderContent = (params: RenderParams) => {
  const useDetailV1Dot1 = isDetailsV1Dot1Enabled()
  const isActive = params.groupItem.status === GROUP_STATUS.ACTIVE
  const isEditEnabled = isActive && params.isAdmin
  const formattedCreatorInfo = {
    creatorName: params.groupItem.creator?.name?.fullName,
    profileImage: params.groupItem.creator?.profilePhotoUrl,
    givenName: params.groupItem.creator?.name?.givenName,
    surname: params.groupItem.creator?.name?.surname,
  }
  const activities = params.groupItem ? params.groupItem?.activities?.edges : []
  const totalCount = params.groupItem?.activities?.totalCount
  const reportLink = params.groupItem.configuration.reportUrl
  const sharedProps = {
    activities,
    formattedCreatorInfo,
    isEditEnabled,
    isSmallerScreen: params.isSmallerScreen,
    reportLink,
    totalCount,
    isAdmin: params.isAdmin,
    isPayPalUser: params.isPayPalUser,
    groupId: params.groupId,
    groupItem: params.groupItem,
    handleContributeAsGuest: params.handleContributeAsGuest,
    handleContributeAsUser: params.handleContributeAsUser,
    handleClickOnEdit: params.handleClickOnEdit,
    handleClickOnReport: params.handleClickOnReport,
    handleClickOnTransfer: params.handleClickOnTransfer,
  }

  if (useDetailV1Dot1) {
    const hasFunds = Number(params.groupItem.currentAmount.value) > 0
    return (
      <GroupDetailV1Dot1
        {...sharedProps}
        isMobileShareSheetOpen={params.isShareSheetOpen}
        handleCloseMobileShareSheet={() => params.setIsShareSheetOpen(false)}
        shareLink={params.groupItem.shareLink}
        isActive={isActive}
        hasFunds={hasFunds}
        handleClickOnClose={params.handleClickOnCloseGroup}
        handleClickOnShare={() => params.setIsShareSheetOpen(true)}
        handleClickOnCreateGroup={params.createGroup}
        navigateHome={params.navigateHome}
        handleClickOnTransfer={params.handleClickOnTransfer}
        displayBackButton={params.isPayPalUser}
        onClickMoreOptions={params.onClickMoreOptions}
      />
    )
  }

  return <GroupDetailV1 {...sharedProps} />
}

const GroupDetail = ({ groupId }: GroupDetailProps) => {
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false)
  const worldReadyC = useContext(WorldReadyContext)
  const { getContent } = useLocalizedContent()
  const isUpdateCoverImage = useAppSelector(
    ({ sheets }) => sheets.updateCoverImage,
  )
  const isUpdateGroupOpened = useAppSelector(({ sheets }) => sheets.updateGroup)
  const isCloseGroupOpened = useAppSelector(({ sheets }) => sheets.closeGroup)
  const { isSmallerScreen } = useWindowSize()
  const dispatch = useAppDispatch()
  const [isQueryDelayed, setQueryDelayed] = useState(true)
  const { data, error, isLoading } = useGetGroupDetailQuery(groupId, {
    skip: isQueryDelayed,
  })
  const isUpdateFlowRef = useRef(false)
  const groupItem = data?.customer?.moneyPools?.moneyPool
  const isPayPalUser = !!serverData?.user
  const isAdmin =
    isPayPalUser &&
    serverData?.user?.encryptedAccountNumber === groupItem?.creator?.id

  const navigateHome = () => {
    tracking(
      Events.p2p_pools_detail_back_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    return navigate(HOME_PATH)
  }

  useEffect(() => {
    if (error) {
      // In case the query returns an error, we set it in the store
      dispatch(setHandledError(error as any)(dispatch))
    } else if (groupItem) {
      Events.updateCommonProps({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      })
      tracking(
        Events.p2p_pools_detail_screen_shown({
          ...serverData.trackingParameters.pxpTracking,
        }),
      )
    }
  }, [error, groupItem, groupId, serverData, isAdmin, isPayPalUser])

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    const externalData = getExternalData()
    if (externalData?.data?.error) {
      dispatch(setHandledError(externalData, getContent)(dispatch))
    } else {
      dispatch(clearHandledError())
    }

    /* This is a hack to delay the query of pool details.
     * this is to allow the asynchronous ALP service some time to insert the Activities  */
    if (window.location.search.includes('from=contribute')) {
      setTimeout(() => {
        setQueryDelayed(false)
      }, serverData.ui.postContributeDelay)
    } else {
      setQueryDelayed(false)
    }
  }, [])

  if (isLoading || isQueryDelayed) {
    return <LoadingSpinner />
  }

  if (error) {
    return null
  }

  const handleContributeAsUser = () => {
    if (serverData?.personalization?.firstTimeUser?.link) {
      dispatch(toggleIntro())
    } else {
      window.location.assign(getContributionUserURL(groupId))
    }
  }

  const handleContributeAsGuest = () => {
    tracking(
      Events.p2p_pools_detail_contribute_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    dispatch(toggleIntro())
  }

  const handleClickOnTransfer = () => {
    tracking(
      Events.p2p_pools_detail_transfer_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    window.location.assign(getTransferURL(groupId))
  }

  const handleClickOnEdit = () => {
    isUpdateFlowRef.current = true
    dispatch(clearHandledError())
    tracking(
      Events.p2p_pools_detail_edit_pool_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )

    /*
    IMPORTANT: must use local browsers timezone when user is editing/updating
    the closeDate.
    If we use serverData.locality.timezone (PayPal Profile timezone),
    then WorldReady will attempt to parse the date in that timezone.
    However in the browser, new Date() will use the browsers timezone.
    This results in a date that is not correct if user is not currently in same timezone
    as their PayPal profile is configured.
    */
    const closeDateString = parseFormattedDate({
      worldReady: worldReadyC,
      value: groupItem.formattedTargetDate,
      timeZone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
    })

    dispatch(
      initializeForm({
        id: groupItem.id,
        title: groupItem.name,
        description: groupItem.description,
        closeDate: closeDateString.toISOString(),
        goalAmount: groupItem?.targetAmount?.value,
        backgroundImageUrl: groupItem.backgroundImageUrl,
      }),
    )
    dispatch(toggleUpdateGroup({ focusOnCoverImageEditButton: false }))
  }

  const handleClickOnReport = () => {
    tracking(
      Events.p2p_pools_detail_report_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
  }

  const handleClickBackOnCoverImage = () => {
    if (isUpdateFlowRef.current) {
      dispatch(toggleUpdateGroup({ focusOnCoverImageEditButton: true }))
    } else {
      dispatch(
        setCreateGroupFlow({
          toggleCreateGroupFlow: true,
          focusOnCoverImageEditButton: true,
        }),
      )
    }
  }

  const handleClickOnCloseGroup = () => {
    tracking(Events.p2p_pools_detail_more_options_close_pressed())
    dispatch(toggleCloseGroup())
  }

  const createGroup = () => {
    tracking(Events.p2p_pools_detail_more_options_create_pressed())
    isUpdateFlowRef.current = false
    if (serverData?.personalization?.firstTimeUser?.link) {
      dispatch(toggleIntro({ source: SHEET_SOURCE.CREATE_GROUP_BUTTON }))
    } else {
      dispatch(clearHandledError())
      dispatch(setCreateGroupFlow({ toggleCreateGroupFlow: true }))
    }
  }

  const onClickMoreOptions = () => {
    tracking(Events.p2p_pools_detail_more_options_pressed())
  }

  return (
    <div className={getContentStyles()}>
      {isUpdateCoverImage && groupItem && (
        <CoverImage
          groupItem={groupItem}
          onClickBack={handleClickBackOnCoverImage}
        />
      )}
      {(isUpdateGroupOpened || isCloseGroupOpened) && groupItem && (
        <Update groupItem={groupItem} />
      )}
      {renderContent({
        isSmallerScreen,
        isShareSheetOpen,
        setIsShareSheetOpen,
        groupId,
        groupItem,
        isAdmin,
        isPayPalUser,
        handleClickOnEdit,
        handleClickOnCloseGroup,
        handleClickOnReport,
        handleContributeAsGuest,
        handleContributeAsUser,
        navigateHome,
        createGroup,
        handleClickOnTransfer,
        onClickMoreOptions,
      })}
    </div>
  )
}

export default GroupDetail
